/**
 * ENG-9046: run an async task at most once at a time, without losing a request.
 *
 * WHAT BREAKS WITHOUT THIS
 * ------------------------
 * `pollCycle()` in `manager-worker.ts` had no in-flight guard, and three
 * Realtime callbacks call `triggerEarlyPoll()`, which schedules it on a
 * `setTimeout(…, 0)`. `triggerEarlyPoll` clears the pending timer first and its
 * docstring claimed the result was idempotent:
 *
 *   "Idempotent — multiple triggers within the same tick coalesce into one
 *    poll because pollCycle() is awaited and scheduleNext() only fires after
 *    it completes."
 *
 * That is true of two triggers in the SAME TICK, and false of a trigger that
 * arrives while a cycle is already RUNNING: `clearTimeout` cancels a pending
 * timer, it cannot cancel a running `pollCycle()`. So every Realtime event
 * during a cycle started ANOTHER full pass over every agent, concurrently.
 *
 * Measured on `agt-aws-1` (16 agents) at 2026-08-17T07:27:38–57Z, before this
 * change: `provision/active-tasks` ran 8× for one agent in 8.6s,
 * `persistent-session-decision` 7× for another with five inside 553ms, and
 * three identical `[bob] Board diff` lines shared a SINGLE MILLISECOND. Every
 * one of those is a database read, against an instance sitting at 88% CPU.
 *
 * WHY NOT THE `livenessInFlight` IDIOM
 * ------------------------------------
 * The heartbeat next door guards with a bare boolean and DROPS the request when
 * busy. Correct there — a missed heartbeat is corrected by the next one. Wrong
 * here: `triggerEarlyPoll` exists precisely so a config or integration change is
 * observed promptly, so dropping the request would trade duplicated work for
 * missed work. This coalesces instead: at most one run in flight, and anything
 * requested during a run is honoured by exactly one follow-up run.
 *
 * The follow-up is a LOOP, not recursion — under sustained event pressure this
 * keeps running useful cycles back to back, which is what the old code did
 * anyway, minus the concurrency.
 *
 * WHAT THIS DID NOT FIX (ENG-9637)
 * --------------------------------
 * Coalescing removed the duplicated WORK. It did not remove the duplicated
 * RE-ARM, and that turned out to be the more expensive half.
 *
 * Every caller of `run()` — coalesced or not — got a promise that resolves when
 * the cycle finishes, and `pollCycleAndReschedule` re-arms the poll timer in a
 * `finally`. So two callers meant two `scheduleNext()` calls, and
 * `scheduleNext()` assigns `pollTimer` WITHOUT clearing it. The single
 * `pollTimer` variable then names only the most recent timer; the other stays
 * armed, untracked, and unreachable — a second self-sustaining poll chain that
 * nothing can ever cancel, because `stopPolling()` clears only the handle it
 * can see.
 *
 * Poll rate becomes N x (1/interval) where N only ever increases within a
 * process lifetime, so the traffic ramps LINEARLY and resets only when the
 * manager restarts. Measured in prod: one host reached ~1 Hz over 13 days and
 * accounted for 53.1% of ALL `/host/refresh` requests on 2026-08-29 (87,542
 * polls/day for one agent against a fleet mean of 2,495), then dropped to
 * baseline the moment a stuck self-update finally restarted the process.
 *
 * A fork needs one COINCIDENCE, not sustained pressure — a single early poll
 * landing mid-cycle is enough, and one every ~14h reproduces the observed
 * slope. That is why rare `triggerEarlyPoll` events in `manager.log` are not
 * evidence against this: frequency was never the relevant variable.
 *
 * Hence `SingleFlightRunResult.coalesced`: the guard already knows which
 * callers it absorbed, so it now TELLS them, and the coalesced caller declines
 * to re-arm because the primary caller already owns that job.
 */

/**
 * ENG-9637: what one `run()` call did, from that caller's point of view.
 *
 * The distinction exists so a caller can tell whether it OWNS the pass it just
 * awaited or merely rode along on someone else's. Any follow-up action that
 * must happen exactly once per pass — re-arming the poll timer is the live
 * example — belongs to the primary caller only.
 */
export interface SingleFlightRunResult {
  /**
   * True when this call was absorbed into an already-running pass rather than
   * starting one. A coalesced caller's request WAS honoured (the in-flight run
   * loops once more to pick it up); it simply is not the owner of the pass.
   */
  coalesced: boolean;
}

export interface SingleFlight {
  /**
   * Run the task, or record that it should run again if one is already in
   * flight. Resolves when this call's work (including any coalesced follow-up
   * it performed) has finished.
   *
   * A COALESCED call resolves when the run it was absorbed into has fully
   * settled — NOT immediately. Every `pollCycle()` call site in
   * `manager-worker.ts` re-arms the poll timer once this resolves, so
   * resolving early would re-arm while the cycle it just joined was still
   * running, partially re-creating the overlap this module exists to remove.
   * (CodeRabbit, #4623)
   *
   * A coalesced call RESOLVES even when the underlying run throws; only the
   * caller that actually started the run sees the rejection. Sharing the
   * rejection would turn one failure into N unhandled rejections — and since
   * no `pollCycle()` call site has a `.catch()`, it is also the difference
   * between one dead poll loop and several.
   *
   * ENG-9637: the resolved value says WHICH kind of call this was. Resolving
   * at the right TIME was never sufficient — a coalesced caller re-arming at
   * the right time is still a second re-arm, and a second re-arm is a second
   * poll chain. See the module docstring.
   */
  run: () => Promise<SingleFlightRunResult>;
  /** True while a run is in progress. Exposed for assertions and diagnostics. */
  isRunning: () => boolean;
  /**
   * How many requests have been absorbed into an in-flight run rather than
   * starting a concurrent one. This is the number the ENG-9046 measurement is
   * about, so it is worth being able to read it.
   */
  coalescedCount: () => number;
}

export function createSingleFlight(
  task: () => Promise<void>,
  opts: { onCoalesce?: (total: number) => void } = {},
): SingleFlight {
  let running = false;
  let rerunRequested = false;
  let coalesced = 0;
  /**
   * Resolves when the current run has fully settled. Deliberately a SEPARATE
   * promise from the one the primary caller gets, for two reasons: it never
   * rejects (see `run` in the interface above), and it is created BEFORE the
   * loop starts, so there is no window in which `running` is true but the
   * promise a coalesced caller would await does not exist yet.
   */
  let settled: Promise<void> | null = null;

  function run(): Promise<SingleFlightRunResult> {
    if (running) {
      // Do NOT start a second pass. Record that the world changed during this
      // one; the loop below will pick it up exactly once when the current run
      // finishes.
      rerunRequested = true;
      coalesced += 1;
      // ENG-9637 (CodeRabbit, #5177): a DIAGNOSTIC observer must not be able to
      // change ownership. If this throws, `run()` never returns
      // `{ coalesced: true }`; `pollCycleAndReschedule` catches the throw,
      // treats coalescing as unknown, and re-arms — on top of the primary's
      // re-arm. That is the forked poll chain this whole change exists to
      // prevent, reconstructed on the error path.
      //
      // Not a theoretical lane. `onCoalesce` is `log()`, whose final
      // `process.stderr.write` is unguarded, and `poll-loop-guard.ts` already
      // names EPIPE/EBADF on a closed or exhausted stderr as a live failure —
      // "a host having a bad day is exactly the host that trips it". A host
      // having a bad day is also the one that can least afford to start
      // doubling its poll rate.
      //
      // Swallowed rather than reported, following the same reasoning as that
      // module's `onError` wrapper: the reporting channel is what just failed,
      // so there is nothing left to report with.
      try {
        opts.onCoalesce?.(coalesced);
      } catch {
        /* deliberately empty: see above */
      }
      // Hand back the in-flight run's completion, so a caller that awaits this
      // is released after the cycle rather than during it — but tagged
      // `coalesced: true` so it knows the pass is not its own to follow up on.
      //
      // ENG-9637: this used to return the bare completion, which made a
      // coalesced caller indistinguishable from the primary one. That is what
      // let both re-arm the poll timer and fork a permanent second chain.
      return (settled ?? Promise.resolve()).then(() => ({ coalesced: true }));
    }

    running = true;
    let markSettled!: () => void;
    settled = new Promise<void>((resolve) => {
      markSettled = resolve;
    });

    const primary = (async () => {
      try {
        do {
          // Cleared BEFORE the await, not after: a request that arrives
          // *during* `task()` must survive into the next iteration. Clearing
          // afterwards would swallow exactly the events this exists to catch.
          rerunRequested = false;
          await task();
        } while (rerunRequested);
      } finally {
        // `finally`, so a throwing task cannot wedge the guard permanently —
        // that would be worse than the duplication, because nothing would poll
        // again. Note this also runs for a task that throws SYNCHRONOUSLY,
        // before `primary` is even assigned, which is why the guard is a plain
        // boolean rather than "is the promise non-null".
        running = false;
        markSettled();
      }
    })();

    // `.then` rather than `await`: a rejection from `primary` must still reject
    // this promise exactly as before, so the caller's error path is unchanged.
    return primary.then(() => ({ coalesced: false }));
  }

  return {
    run,
    isRunning: () => running,
    coalescedCount: () => coalesced,
  };
}
